import AuthenticatedController from './AuthenticatedController';
import {
  ASSETS_UPLOADED,
  LANDING_PAGE_LOGO,
  UPLOAD_AUDIO,
  UPLOAD_EMAIL_ATTACHMENT,
  UPLOAD_FILE,
  UPLOAD_IMAGE,
  UPLOAD_VIDEO,
} from '@mm/api-routes';

const fileTypeDictionary = {
  pdf: 'pdf',
};

const getExtensionFromFileName = filename => {
  if (filename) {
    return filename.split('.').pop();
  }

  return '';
};

const nameWithoutExtension = name =>
  name.replace(/\.[^/.]+$/, '').replace(/[<>"#%{}|^~['\]\\]/g, '');

export default class UploadController extends AuthenticatedController {
  static REQUEST_TIMEOUT = 1000;

  static async uploadImage(imageInfo, companyId) {
    const imageName = imageInfo.content.name;
    const query = {
      original_extension: imageInfo.extension,
      source_size: imageInfo.content.size,
      company_id: companyId,
      title: (imageName && nameWithoutExtension(imageName)) || '',
    };

    return super
      .get(UPLOAD_IMAGE, query)
      .then(response => response.image)
      .then(image => {
        const request = new Request(image.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': imageInfo.content.type },
          body: imageInfo.content,
        });

        return fetch(request).then(
          () =>
            new Promise(resolve => {
              const startInterval = () =>
                setTimeout(
                  () =>
                    fetch(image.urls.medium, {
                      method: 'HEAD',
                    }).then(response =>
                      response.ok ? resolve(image) : startInterval(),
                    ),
                  UploadController.REQUEST_TIMEOUT,
                );
              startInterval();
            }),
        );
      });
  }

  static async uploadLandingPageLogo(imageInfo, landingPageId) {
    const imageName = imageInfo.content.name;

    const presignedUploadLinkRequest = {
      extension: imageInfo.extension,
      source_size: imageInfo.content.size,
      id: landingPageId,
      title: imageName && nameWithoutExtension(imageName),
    };

    const logoLinkResponse = await super.get(
      LANDING_PAGE_LOGO(5),
      presignedUploadLinkRequest,
    );

    const {
      links: { uploadLink, newLogoUrl },
    } = logoLinkResponse;
    const s3uploadRequest = new Request(uploadLink, {
      method: 'PUT',
      headers: { 'Content-Type': imageInfo.content.type },
      body: imageInfo.content,
    });

    await fetch(s3uploadRequest);

    return newLogoUrl;
  }

  static async uploadVideo(videoInfo, companyId) {
    const query = {
      original_extension: videoInfo.extension,
      source_size: videoInfo.content.size,
      company_id: companyId,
      title: nameWithoutExtension(videoInfo.content.name),
    };
    const { video } = await super.get(UPLOAD_VIDEO, query);
    const request = new Request(video.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': videoInfo.content.type },
      body: videoInfo.content,
    });
    await fetch(request);
    return video;
  }

  static async uploadAudio(audioInfo, companyId) {
    const query = {
      original_extension: audioInfo.extension,
      source_size: audioInfo.content.size,
      company_id: companyId,
      title: nameWithoutExtension(audioInfo.content.name),
    };
    const { audio } = await super.get(UPLOAD_AUDIO, query);
    const request = new Request(audio.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': audioInfo.content.type },
      body: audioInfo.content,
    });
    await fetch(request);
    return audio;
  }

  static async uploadFile(fileInfo, companyId) {
    const { content, extension } = fileInfo;
    const fileExtension = getExtensionFromFileName(content && content.name);
    const query = {
      original_extension:
        fileExtension || fileTypeDictionary[extension] || extension,
      source_size: content.size,
      company_id: companyId,
      title: nameWithoutExtension(content.name),
    };
    const { attachment } = await super.get(UPLOAD_FILE, query);
    const request = new Request(attachment.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': content.type },
      body: content,
    });
    await fetch(request);
    await super.put(ASSETS_UPLOADED(attachment.id));
    return attachment;
  }

  static async uploadEmailAttachment(fileInfo, companyId) {
    const { content, extension } = fileInfo;
    const fileExtension = getExtensionFromFileName(content && content.name);
    const query = {
      original_extension:
        fileExtension || fileTypeDictionary[extension] || extension,
      source_size: content.size,
      company_id: companyId,
      title: nameWithoutExtension(content.name),
    };
    const { emailAttachment } = await super.get(UPLOAD_EMAIL_ATTACHMENT, query);
    const request = new Request(emailAttachment.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': content.type },
      body: content,
    });
    await fetch(request);
    await super.put(ASSETS_UPLOADED(emailAttachment.id));
    return emailAttachment;
  }
}
